import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import RunoutPeriodDropdown from 'adminDashboard/benefitDetails/common/RunoutPeriodDropdown'
import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  BenefitDetailsResponse,
  BenefitProductClassResponse,
  FinalizeIchraBenefitDesignRequest,
  deleteIchraProductClass,
  finalizeIchraBenefitDesign,
  getBenefitDetails,
  getBenefitProductClasses,
  getIchraProductClassDesignWizard,
  getIchraProductClassDesignWizardState,
  setPlanPeriodRunoutDays,
} from 'core'

import { Card, Col, DeleteConfirmModal, Row, SimpleSelect, Skeleton } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import ProductClassList from '../../../common/ProductClassList'
import ProductClassWizardModal from '../../../common/productClassWizardModal'
import { BaseComponentProps } from '../../common/models'
import {
  IchraProductClassWizard,
  IchraProductClassWizardNavItemsMapping,
} from './productClassWizardModal/ProductClassStepMapping'

import './styles.less'

type IchraFinishDesignBenefitProps = {
  planPeriodId: string
  benefitStartDate: string
  benefitEndDate: string
}

type ProductClassModalStateProps = {
  productId?: string
  visible: boolean
  editingStep?: string
  name?: string
}

const IchraFinishDesignBenefit: React.FC<BaseComponentProps<IchraFinishDesignBenefitProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams() as any
  const [rounoutDays, setRunoutDays] = useState<number>(60)

  const [deleteProductClass, setDeleteProductClass] = useState<BenefitProductClassResponse | null>(null)
  const [productClassModalState, setProductClassModalState] = useState<ProductClassModalStateProps>({
    visible: false,
  })

  const {
    data: productClasses,
    reload: reloadProductClasses,
    isLoading: isProductClassesLoading,
  } = useQueryRequestV2<BenefitProductClassResponse[]>({
    queryFn: () => getBenefitProductClasses(planPeriod),
  })

  const { data: planPeriodDetails } = useQueryRequestV2<BenefitDetailsResponse>({
    initialState: null,
    queryFn: () => getBenefitDetails(planPeriod),
    enable: !!planPeriod,
  })

  const { sendCommand: sendDeleteProductClass, isLoading: isDeleteProductClassLoading } = useCommandRequestV2(
    (productClassId: string) => deleteIchraProductClass(productClassId),
  )

  const { sendCommand: sendFinalizeBenefit } = useCommandRequestV2((request: FinalizeIchraBenefitDesignRequest) =>
    finalizeIchraBenefitDesign(request),
  )

  const { sendCommand: sendPlanPeriodRunoutDays } = useCommandRequestV2((runOutDays: number) =>
    setPlanPeriodRunoutDays({
      planPeriodId: props.state.planPeriodId,
      runOutDays,
    }),
  )

  useEffect(() => {
    const existClassThatIsNoTDesigned = productClasses?.some((x) => !x.designCompleted)
    props.setIsFormValid(!existClassThatIsNoTDesigned && productClasses?.length > 0)
  }, [productClasses])

  const onDeleteProductClass = async () => {
    const result = await sendDeleteProductClass(deleteProductClass?.id)
    if (result.completed) {
      setDeleteProductClass(null)
      await reloadProductClasses()
    }
  }

  const onProductClassWizardModalClosed = async () => {
    await reloadProductClasses()

    setProductClassModalState({
      ...productClassModalState,
      visible: false,
    })
  }

  const onBenefitDesignEdit = (item: BenefitProductClassResponse) => {
    setProductClassModalState({
      productId: item.id,
      name: item.name,
      visible: true,
      editingStep: IchraProductClassWizard.SetEligibleExpenses,
    })
  }

  const onSubmit = async () => {
    const result = await sendFinalizeBenefit({ planPeriodId: planPeriod })

    if (result.completed) {
      props.finish()
    } else {
      props.setIsSubmiting(false)
    }
  }

  useEffect(() => {
    if (planPeriodDetails) {
      setRunoutDays(planPeriodDetails.runOutDays)
    }
  }, [planPeriodDetails])

  return (
    <>
      {productClassModalState.visible && (
        <ProductClassWizardModal
          navigationMapping={IchraProductClassWizardNavItemsMapping}
          getProductClassWizard={(planPeriodId, productId) => getIchraProductClassDesignWizard(productId)}
          getProductClassWizardState={(planPeriodId, productId) => getIchraProductClassDesignWizardState(productId)}
          visible={productClassModalState.visible}
          id={productClassModalState.productId}
          planPeriodId={props.state.planPeriodId}
          afterClose={async () => await onProductClassWizardModalClosed()}
          editingStep={productClassModalState.editingStep}
          finishWizardMessage={`${translate(
            'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.finish-popup-class-design-wizard-message',
          ).replace(':className', productClassModalState.name)}`}
        />
      )}

      <DeleteConfirmModal
        title={`Deleting '${deleteProductClass?.name}' ...`}
        content={`Are you sure you want to delete ${deleteProductClass?.name}? This action cannot be undone.`}
        onDelete={onDeleteProductClass}
        onCancel={() => {
          setDeleteProductClass(null)
        }}
        deleteButtonLoading={isDeleteProductClassLoading}
        visible={!!deleteProductClass}
      />

      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={props.state}
        onSubmit={onSubmit}
        validate={(values) => {
          props.hasDirtyData(true)
          props.setIsFormValid(productClasses.length > 0)
          return {}
        }}
      >
        {(formik) => {
          const { handleSubmit, submitForm } = formik
          bindSubmitFormHandler(submitForm)
          return (
            <form onSubmit={handleSubmit} autoComplete="off" className={'ichra-benefit-design-wrapper'}>
              <Row className={'mb-32'}>
                <Col span={17} className={'benefit_design_page_header'}>
                  <h5>Design a benefit for each employee class</h5>
                  <h6 className={'inline mr-8'}>
                    Each employee class can have different benefit categories and allowance amounts which you'll design
                    here. You’ll have the opportunity to add employees for each of these classes in a later step.
                  </h6>
                </Col>
              </Row>

              <Skeleton loading={isProductClassesLoading}>
                {productClasses && (
                  <ProductClassList
                    productClasses={productClasses}
                    planPeriodId={planPeriod}
                    onEdit={onBenefitDesignEdit}
                    onDesignClass={onBenefitDesignEdit}
                    onDelete={(item) => setDeleteProductClass(item)}
                  />
                )}
              </Skeleton>
              {productClasses && (
                <RunoutPeriodDropdown
                  rounoutDays={rounoutDays}
                  onChange={(value) => {
                    setRunoutDays(value)
                    sendPlanPeriodRunoutDays(value)
                  }}
                />
              )}
            </form>
          )
        }}
      </Formik>
    </>
  )
}

const formValidator = (translate: any) => Yup.object({})

export default IchraFinishDesignBenefit
